import { useEffect, useRef, useState } from 'react';

/**
 * Options for {@link useSpinDelay}.
 */
export interface UseSpinDelayOptions {
  /** Wait this many ms before showing the spinner. Defaults to 500. */
  delay?: number;
  /** Once shown, keep the spinner visible for at least this long.
   *  Defaults to 200ms. Prevents the spinner from flashing on fast
   *  responses. */
  minDuration?: number;
}

const DEFAULT_DELAY = 500;
const DEFAULT_MIN_DURATION = 200;

/**
 * Smooths a boolean loading flag into a "should show spinner" flag that
 * avoids two common UX glitches:
 *   1. **Spinner flash** on fast loads (under `delay`).
 *   2. **Spinner flicker** when the load completes immediately after
 *      the spinner appears (held for at least `minDuration`).
 *
 * @example
 * ```tsx
 * const isPending = useIsPending();
 * const showSpinner = useSpinDelay(isPending);
 * return showSpinner ? <Spinner /> : <SubmitButton />;
 * ```
 */
export function useSpinDelay(
  loading: boolean,
  {
    delay = DEFAULT_DELAY,
    minDuration = DEFAULT_MIN_DURATION,
  }: UseSpinDelayOptions = {},
) {
  const [visible, setVisible] = useState(false);
  const shownAtRef = useRef<number | null>(null);
  const delayTimeoutRef = useRef<number | null>(null);
  const hideTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const clearDelayTimer = () => {
      if (delayTimeoutRef.current !== null) {
        window.clearTimeout(delayTimeoutRef.current);
        delayTimeoutRef.current = null;
      }
    };

    const clearHideTimer = () => {
      if (hideTimeoutRef.current !== null) {
        window.clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    };

    if (loading) {
      clearHideTimer();
      if (visible) return;

      clearDelayTimer();
      delayTimeoutRef.current = window.setTimeout(
        () => {
          shownAtRef.current = Date.now();
          setVisible(true);
          delayTimeoutRef.current = null;
        },
        Math.max(0, delay),
      );
      return;
    }

    clearDelayTimer();

    if (!visible) return;

    const shownAt = shownAtRef.current ?? Date.now();
    const elapsed = Date.now() - shownAt;
    const remaining = Math.max(0, minDuration - elapsed);

    clearHideTimer();
    if (remaining === 0) {
      hideTimeoutRef.current = window.setTimeout(() => {
        shownAtRef.current = null;
        setVisible(false);
        hideTimeoutRef.current = null;
      }, 0);
      return;
    }

    hideTimeoutRef.current = window.setTimeout(() => {
      shownAtRef.current = null;
      setVisible(false);
      hideTimeoutRef.current = null;
    }, remaining);
  }, [delay, loading, minDuration, visible]);

  useEffect(() => {
    return () => {
      if (delayTimeoutRef.current !== null) {
        window.clearTimeout(delayTimeoutRef.current);
      }
      if (hideTimeoutRef.current !== null) {
        window.clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  return visible;
}
